import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const diffusionCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'Diffusion',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.productDeliveryLocationDiffusion.`,
});

function buildCacheKey({ productId, deliveryLocationId, minDate, maxDate, latestDate }) {
	return `diffusions.${productId ?? 'all'}.${deliveryLocationId ?? 'all'}.${minDate ?? '_'}.${maxDate ?? '_'}.${latestDate ?? '_'}`;
}

function buildRequestUrl({ productId, deliveryLocationId, minDate, maxDate, latestDate }) {
	const params = new URLSearchParams();
	if (productId != null) params.append('productId', productId);
	if (deliveryLocationId != null) params.append('deliveryLocationId', deliveryLocationId);
	if (minDate) params.append('minDate', minDate);
	if (maxDate) params.append('maxDate', maxDate);
	if (latestDate != null) params.append('latestDate', latestDate);

	const query = params.toString();
	return getApiUrl('Assort/ProductDeliveryLocationDiffusions') + (query ? `?${query}` : '');
}

/**
 * Get sell-out (datasharing) diffusions for a product / delivery location.
 * Each row holds: date, productId, ean, productReference, productLabel, volume, value, vmm, vmh.
 * Offline-first: served from memory cache, then IndexedDB, then API (with background refresh).
 */
export async function getProductDeliveryLocationDiffusions(filters = {}, forceRefresh = false) {
	const cacheKey = buildCacheKey(filters);

	if (!forceRefresh) {
		const memCached = diffusionCache.getFromMemory(cacheKey);
		if (memCached) return memCached;
	}

	try {
		const cached = await diffusionCache.get(cacheKey);
		if (cached && !forceRefresh) {
			diffusionCache.setInMemory(cacheKey, cached);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'Diffusion', '📴 Offline: Returning cached product delivery location diffusions');
				return cached;
			}

			setTimeout(() => refreshDiffusionsInBackground(filters, cacheKey), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Diffusion', '📴 Offline: No cached product delivery location diffusions available');
			return [];
		}

		return await fetchDiffusionsFromAPI(filters, cacheKey);
	} catch (error) {
		log('error', 'Diffusion', `❌ Error getting product delivery location diffusions: ${error.message}`);

		try {
			const fallback = await diffusionCache.get(cacheKey);
			if (fallback) {
				log('info', 'Diffusion', '💾 Using fallback cached product delivery location diffusions');
				return fallback;
			}
		} catch (cacheError) {
			log('error', 'Diffusion', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function requestDiffusions(filters) {
	const response = await authenticatedFetch(buildRequestUrl(filters), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	return (await response.json()) || [];
}

async function fetchDiffusionsFromAPI(filters, cacheKey) {
	try {
		const diffusions = await requestDiffusions(filters);
		await diffusionCache.put(cacheKey, diffusions);
		diffusionCache.setInMemory(cacheKey, diffusions);

		log('success', 'Diffusion', `✅ Fetched and cached ${diffusions.length} product delivery location diffusions`);
		return diffusions;
	} catch (error) {
		log('error', 'Diffusion', `❌ Failed to fetch product delivery location diffusions from API: ${error.message}`);
		throw error;
	}
}

async function refreshDiffusionsInBackground(filters, cacheKey) {
	try {
		await fetchDiffusionsFromAPI(filters, cacheKey);
		log('success', 'Diffusion', '🔄 Background refresh of product delivery location diffusions completed');
	} catch (error) {
		log('warn', 'Diffusion', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshProductDeliveryLocationDiffusions() {
	diffusionCache.clearMemory();
	await diffusionCache.clear();
}

const SELLOUT_OFFLINE_WINDOW_MONTHS = 6;

function sellOutPreloadMinDate() {
	const date = new Date();
	date.setMonth(date.getMonth() - SELLOUT_OFFLINE_WINDOW_MONTHS);
	return date.toISOString();
}

/**
 * Preload sell-out diffusions for offline use during the Vue background sync, so the DataSharing
 * tab (RG #23004 §7) has data available offline even if the user never opened it while online.
 *
 * One bulk request (no deliveryLocationId filter) instead of one request per store, bounded to the
 * last 6 months (iOS Jetsam — tasks/lessons.md #48-52). The endpoint returns the whole territory,
 * so we bucket the response by row.deliveryLocationId and cache only the rep's own stores
 * (deliveryLocationIds) to keep the persisted footprint bounded. Each store is cached under the
 * same key as the unbounded on-demand lookup (deliveryLocationId only, no date bounds) so both
 * paths share one entry — an online visit later refreshes it to the full history in the background.
 */
export async function preloadAllProductDeliveryLocationDiffusions(deliveryLocationIds = []) {
	if (!iframeDataBridge.isOnline()) return;
	if (!Array.isArray(deliveryLocationIds) || deliveryLocationIds.length === 0) return;

	try {
		const rows = await requestDiffusions({ minDate: sellOutPreloadMinDate() });
		debugger;
		const byDeliveryLocation = new Map(deliveryLocationIds.map((id) => [id, []]));
		for (const row of rows) {
			const bucket = byDeliveryLocation.get(row?.deliveryLocationId);
			if (bucket) bucket.push(row);
		}

		for (const [deliveryLocationId, diffusions] of byDeliveryLocation) {
			await diffusionCache.put(buildCacheKey({ deliveryLocationId }), diffusions);
		}

		log('success', 'Diffusion', `✅ Preloaded sell-out diffusions for ${byDeliveryLocation.size} delivery locations in one bulk request`);
	} catch (error) {
		log('warn', 'Diffusion', `⚠️ Failed to bulk-preload sell-out diffusions: ${error.message}`);
	}
}
